import { Alert, Divider, FormControl } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormControls,
  FormikArray,
  FormikNumericField,
  FormikSelect,
  FormikSelectOption,
  HelpTooltip,
} from 'components';
import { ShopIntegrationType } from 'enums';
import {
  useCurrencies,
  useMeta,
  useUserContext,
  useUserContextUtils,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Shop } from 'types';

type Props = Pick<
  Shop,
  'assetCurrencyId' | 'payinCurrencyExchangeId' | 'integrationConfig'
>;

type Values = Pick<Shop, 'payinTradeMethodsConfigs'>;
type WebhookOverride = NonNullable<Shop['webhookOverrides']>[number];

type RowProps = {
  index: number;
  item: WebhookOverride;
  currencyOptions: FormikSelectOption[];
  paymentTypeOptions: FormikSelectOption[];
};

const ShopWebhookOverrideRow: React.FC<RowProps> = ({
  index,
  item,
  currencyOptions,
  paymentTypeOptions,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage.sections.webhook_overrides',
  });
  const { fiatCurrenciesOptions } = useCurrencies();
  const { paymentTypesOptions } = useUserContextUtils();
  const rowOptions = useMemo(() => {
    const preserveSelectedOption = (
      filteredOptions: FormikSelectOption[],
      allOptions: FormikSelectOption[],
      selectedValue: string,
    ) => {
      if (
        !selectedValue ||
        filteredOptions.some((option) => option.value === selectedValue)
      ) {
        return filteredOptions;
      }

      return [
        ...filteredOptions,
        allOptions.find((option) => option.value === selectedValue) || {
          value: selectedValue,
          label: selectedValue,
        },
      ];
    };

    return {
      currencies: preserveSelectedOption(
        currencyOptions,
        fiatCurrenciesOptions,
        item.orderCurrencyId,
      ),
      paymentTypes: preserveSelectedOption(
        paymentTypeOptions,
        paymentTypesOptions,
        item.orderPaymentTypeId,
      ),
    };
  }, [
    currencyOptions,
    fiatCurrenciesOptions,
    item.orderCurrencyId,
    paymentTypeOptions,
    paymentTypesOptions,
    item.orderPaymentTypeId,
  ]);

  return (
    <FormControls sx={{ flex: 1 }}>
      <FormikSelect
        name={`webhookOverrides[${index}].orderCurrencyId`}
        label={t('currency')}
        options={rowOptions.currencies}
        noneOption
        fullWidth
      />
      <FormikSelect
        name={`webhookOverrides[${index}].orderPaymentTypeId`}
        label={t('payment_type')}
        options={rowOptions.paymentTypes}
        noneOption
        fullWidth
      />
      <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
        <FormikNumericField
          name={`webhookOverrides[${index}].amountOffset`}
          label={t('amount_offset')}
          allowNegative={false}
          required
          fullWidth
        />
        <HelpTooltip
          title={
            <Fragment>
              {t('description')}
              <br />
              {t('details')}
            </Fragment>
          }
        />
      </FormControl>
    </FormControls>
  );
};

export const ShopWebhookOverrides: React.FC<Props> = ({
  assetCurrencyId,
  payinCurrencyExchangeId,
  integrationConfig,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage.sections.webhook_overrides',
  });
  const { isShopWebhookOverridesEnabled } = useMeta();
  const { values } = useFormikContext<Values>();
  const { fiatCurrenciesOptions, currencyExchanges } = useCurrencies();
  const { tradeMethods } = useUserContext();
  const { paymentTypesOptions } = useUserContextUtils();

  const filteredCurrencyOptions = useMemo(() => {
    const filteredCurrencyExchanges = currencyExchanges.filter(
      (exchange) =>
        exchange.enabled &&
        exchange.assetCurrencyId === assetCurrencyId &&
        (!payinCurrencyExchangeId || exchange.id === payinCurrencyExchangeId),
    );
    const filteredCurrencyIds = new Set(
      filteredCurrencyExchanges
        .filter((exchange) => {
          const defaultExchange = filteredCurrencyExchanges.find(
            (candidate) =>
              candidate.fiatCurrencyId === exchange.fiatCurrencyId &&
              candidate.defaultExchange,
          );

          return !!(!payinCurrencyExchangeId && defaultExchange
            ? defaultExchange.price
            : exchange.price);
        })
        .map((exchange) => exchange.fiatCurrencyId),
    );

    return fiatCurrenciesOptions.filter((option) =>
      filteredCurrencyIds.has(option.value),
    );
  }, [
    currencyExchanges,
    assetCurrencyId,
    payinCurrencyExchangeId,
    fiatCurrenciesOptions,
  ]);

  const filteredPaymentTypeOptions = useMemo(() => {
    const configuredTradeMethodIds = new Set(
      values.payinTradeMethodsConfigs?.map((config) => config.tradeMethodId),
    );
    const filteredPaymentTypeIds = new Set(
      tradeMethods
        .filter((method) => configuredTradeMethodIds.has(method.id))
        .map((method) => method.paymentTypeId),
    );

    return paymentTypesOptions.filter((option) =>
      filteredPaymentTypeIds.has(option.value),
    );
  }, [values.payinTradeMethodsConfigs, tradeMethods, paymentTypesOptions]);

  const renderItemComponent = useCallback(
    ({ index, item }: { index: number; item: WebhookOverride }) => (
      <ShopWebhookOverrideRow
        index={index}
        item={item}
        currencyOptions={filteredCurrencyOptions}
        paymentTypeOptions={filteredPaymentTypeOptions}
      />
    ),
    [filteredCurrencyOptions, filteredPaymentTypeOptions],
  );

  if (!isShopWebhookOverridesEnabled) {
    return null;
  }

  return (
    <Fragment>
      <Divider>{t('title')}</Divider>
      {integrationConfig?.enabled &&
        integrationConfig.type === ShopIntegrationType.RosaOne && (
          <Alert severity="warning">{t('rosa_one_warning')}</Alert>
        )}
      <FormikArray
        name="webhookOverrides"
        addItemLabel={t('add_rule')}
        removable
        ItemComponent={renderItemComponent}
        defaultValue={{
          orderCurrencyId: '',
          orderPaymentTypeId: '',
          amountOffset: 0,
        }}
      />
    </Fragment>
  );
};
